import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { capitalize } from '@core/util/string';
import {
	Matter,
	MatterClientType,
	MatterFileType,
	MatterTitle,
} from '@models/matter';
import { ClientType, PersonClient } from '@models/clients';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SaleClientsRepoService } from '@repos/businesses/sales/client.service';
import { MatterRepo } from '@repos/matter/matter.repo';
import { ClientsRepoService } from '@repos/clients/clients.service';
import { AuthState } from '@state/auth';
import { AuthUser } from '@state/auth/auth.models';
import { take } from 'rxjs/operators';
import { MatterTitlesRepoService } from '@repos/matter-titles/matter-titles-repo.service';
import { ClientCreationService } from '@services/closer-api/client-creation/client-creation.service';
import { DocketSearchDetail } from '@models/teranet';

export interface SyncDataRequest {
	updateClients: {
		id: string;
		name: string;
		add: boolean;
	}[];
	titlesToSync?: MatterTitle[];
}

@Injectable({
	providedIn: 'root',
})
export class TeranetRepoService {
	@SelectSnapshot(AuthState.getLoggedInUser) authUser: AuthUser;

	constructor(
		private matterRepo: MatterRepo,
		private saleClientsRepo: SaleClientsRepoService,
		private clientsRepo: ClientsRepoService,
		private db: AngularFirestore,
		private matterTitleRepo: MatterTitlesRepoService,
		private clientCreationService: ClientCreationService,
	) {}

	async SyncDetails(
		syncDetails: SyncDataRequest,
		matterId: string,
		matterFileType: MatterFileType,
	): Promise<void> {
		const batch = this.db.firestore.batch();

		if (syncDetails.titlesToSync?.length > 0) {
			syncDetails.titlesToSync.forEach((x) => {
				const id: string = x.id;
				const matterParcelsPath = this.matterTitleRepo.DocPath(id, {
					matterId,
				});
				batch.set(this.db.firestore.doc(matterParcelsPath), {
					...x,
					timeAdded: new Date().getTime(),
				} as MatterTitle);
			});
		}

		for await (const client of syncDetails.updateClients) {
			const _clientDoc = await this.clientsRepo
				.Get(client.id, null)
				.pipe(take(1))
				.toPromise();
			const _client = _clientDoc.data();
			const clientDocPath = this.clientsRepo.DocPath(client.id, null);

			if (_client.type === ClientType.PERSON) {
				const updatedClientNames = this.GetClientName(client.name);
				batch.update(this.db.firestore.doc(clientDocPath), {
					...updatedClientNames,
					lastUpdatedBy: { name: this.authUser.name, id: this.authUser.uid },
				});
			} else {
				batch.update(this.db.firestore.doc(clientDocPath), {
					name: client.name,
					lastUpdatedBy: { name: this.authUser.name, id: this.authUser.uid },
				});
			}

			if (client.add) {
				await this.clientCreationService.AddExistingClientToMatter({
					clientId: client.id,
					matterId,
					matterClientType:
						matterFileType === MatterFileType.REFINANCE
							? MatterClientType.BORROWER
							: MatterClientType.SELLER,
				});
			}
		}

		return batch.commit();
	}

	GetClientName(name: string): Partial<PersonClient> {
		const _name = capitalize(name);
		let firstName;
		let middleNames;
		let lastName;

		if (_name.includes(',')) {
			const parts = _name.split(',');

			lastName = parts[0].trim();

			if (parts.length > 1) {
				const firstMiddles = parts[1].trim().split(' ');

				firstName = firstMiddles.shift().trim();

				if (firstMiddles.length > 0) {
					middleNames = firstMiddles.join(' ').trim();
				}
			}
		} else {
			firstName = _name;
		}

		return {
			firstName,
			middleNames: middleNames ?? '',
			lastName,
		};
	}

	AssignDocket(matterId: string, docket: DocketSearchDetail): Promise<void> {
		const matter: Partial<Matter> = {
			teranetDocket: {
				docketId: docket.docket_id,
				docketName: docket.docket_name,
				docketTeranetSystemId: docket.docket_system_id.toString(),
			},
		};

		return this.matterRepo.Update(matterId, matter);
	}
}
